import {
  QuestionnaireGroupResponse,
  SelectionRequest,
} from '@tiendanube/common';

interface Params {
  currentStep: number;
  nextStep: number;
  currentGroup: QuestionnaireGroupResponse;
  currentValues: SelectionRequest[];
}

/**
 * Extracts categories from selected answers to filter subsequent questions.
 * Backend-driven implementation controlled by the `propagateCategory` flag.
 *
 * The backend controls which questions filter the next by setting `propagateCategory: true`.
 * This prevents unintended filtering chains (e.g., Q1 → Q2 ✅, but not Q2 → Q3).
 *
 * @param currentStep - Current question index
 * @param nextStep - Next question index
 * @param currentGroup - Current question configuration
 * @param currentValues - User's selected answer(s)
 * @returns Array of category strings to filter next question, or empty array if no filtering
 */
export function getNextStepCategory({
  currentStep,
  nextStep,
  currentGroup,
  currentValues,
}: Params): string[] {
  // Only propagate categories if moving to the next sequential step
  // (not jumping, not skipping, not going back)
  if (nextStep !== currentStep + 1) {
    return [];
  }

  // Check if this question is configured to propagate categories
  // Backward compatible: undefined or false means no propagation
  if (!currentGroup.propagateCategory) {
    return [];
  }

  // No answers selected or empty selection
  if (!currentValues || currentValues.length === 0) {
    return [];
  }

  // Extract categories from selected options
  const categories = currentValues
    .map((value) => {
      const selectedOption = currentGroup.types.find(
        (type) => type.id === value.domain_mapping_id,
      );
      return selectedOption?.category;
    })
    .filter((category): category is string => category !== undefined);

  // Remove duplicates in case multiple answers have the same category
  return [...new Set(categories)];
}
